/** Maximum number of records submitted in a single import request. */
import * as moment from "moment";
import { PSEA_CORE_STANDARDS } from "../pseaCoreStandards";

export const MAX_IMPORT_RECORDS = 2000;

type StandardNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export type PseaAssessmentFieldKey =
  | "Contractor"
  | "Agency"
  | "DateOfAssessment"
  | "OverallRating"
  | `CS${StandardNumber}Met`
  | `CS${StandardNumber}Comment`;

/** The rating bands accepted in the Overall Rating column. */
export const PSEA_RATING_OPTIONS = [
  "Full Capacity",
  "Medium Capacity",
  "Low Capacity",
];

export type FieldMapping = Partial<Record<PseaAssessmentFieldKey, string>>;

export type ImportStatus = "idle" | "running" | "done";

export type ImportResults = {
  total: number;
  successful: number;
  failed: number;
  downloadUrl: string | null;
};

/**
 * Columns that must be mapped before the import can run. Everything else is
 * optional: an unmapped core standard is imported unanswered, which is a real
 * state on the form and can be filled in afterwards.
 */
export const REQUIRED_FIELDS: PseaAssessmentFieldKey[] = [
  "Contractor",
  "Agency",
  "DateOfAssessment",
];

export const PSEA_ASSESSMENT_FIELDS: Array<{
  key: PseaAssessmentFieldKey;
  label: string;
  required?: boolean;
}> = [
  { key: "Contractor", label: "Contractor", required: true },
  { key: "Agency", label: "Assessing Agency", required: true },
  { key: "DateOfAssessment", label: "Date of Assessment", required: true },
  // Optional. When filled in it overrides the rating computed from the core
  // standards, and it is the only outcome a legacy row carries.
  { key: "OverallRating", label: "Overall Rating", required: false },
  // Two columns per core standard, labelled with the standard's own title so
  // the mapping table reads the way the assessment form does.
  ...PSEA_CORE_STANDARDS.flatMap((standard) => {
    const n = standard.number as StandardNumber;
    return [
      {
        key: `CS${n}Met` as PseaAssessmentFieldKey,
        label: `CS${n} ${standard.title} — Met`,
      },
      {
        key: `CS${n}Comment` as PseaAssessmentFieldKey,
        label: `CS${n} ${standard.title} — Comment`,
      },
    ];
  }),
];

function normalizeStr(s: string): string {
  return s
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function similarityScore(a: string, b: string): number {
  if (a === b) return 2;
  if (a.includes(b) || b.includes(a)) {
    return Math.min(a.length, b.length) / Math.max(a.length, b.length);
  }
  return 0;
}

export function autoMapFields(csvHeaders: string[]): FieldMapping {
  const mapping: FieldMapping = {};
  const claimed = new Set<string>();

  for (const field of PSEA_ASSESSMENT_FIELDS) {
    const normField = normalizeStr(field.key);
    let bestHeader = "";
    let bestScore = 0;

    for (const header of csvHeaders) {
      if (claimed.has(header)) continue;
      const score = similarityScore(normField, normalizeStr(header));
      if (score > bestScore) {
        bestScore = score;
        bestHeader = header;
      }
    }

    if (bestScore > 0) {
      mapping[field.key] = bestHeader;
      claimed.add(bestHeader);
    }
  }

  return mapping;
}

/**
 * Flattens a mapped row into the shape the import endpoint expects. Core
 * standards stay as flat `cs1Met` / `cs1Comment` keys rather than a nested
 * array so that the failed-rows CSV the server returns has one readable column
 * per value.
 */
export function mapRowToRecord(
  row: Record<string, string>,
  mappings: FieldMapping
) {
  const get = (key: PseaAssessmentFieldKey): string | null => {
    const col = mappings[key];
    if (!col) return null;
    return row[col]?.trim() || null;
  };

  const record: Record<string, any> = {
    contractor: get("Contractor"),
    agency: get("Agency"),
    dateOfAssessment:
      (get("DateOfAssessment") &&
        moment(get("DateOfAssessment"), "DD/MM/YYYY")
          .toDate()
          .toLocaleDateString("en-CA")) ||
      null,
    overallRating: get("OverallRating") ?? "",
  };

  for (const standard of PSEA_CORE_STANDARDS) {
    const n = standard.number as StandardNumber;
    record[`cs${n}Met`] = get(`CS${n}Met`) ?? "";
    record[`cs${n}Comment`] = get(`CS${n}Comment`) ?? "";
  }

  return record;
}
